<div class="p-1">
  <!-- Shared header -->
  <div class="flex items-start justify-between mb-4">
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-center w-10 h-10 bg-bulk_Bg rounded">
        <i class="pi pi-bell text-bulkSelected"></i>
      </div>
      <span class="text-2xl font-bold text-main">Monthly Report Notification</span>
    </div>
    <button class="text-Gray-1000 p-1 rounded transition-colors" (click)="close()">
      <i class="pi pi-times"></i>
    </button>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="flex justify-center items-center py-10">
    <i class="pi pi-spin pi-spinner text-2xl text-main"></i>
  </div>

  <ng-container *ngIf="!isLoading && status && !result">
    <p class="text-sm text-Gray-800 mb-5">
      Send bulk email notification to <strong class="font-bold text-main">all active credit union portal users</strong>
      about published monthly reports
    </p>

    <div class="flex items-start gap-3 rounded-lg p-4 mb-6" [ngClass]="{
        'bg-green-50': status.reasonCode === 'AVAILABLE',
        'bg-red-50':   status.reasonCode === 'REPORTS_NOT_PUBLISHED' || status.reasonCode === 'PREVIOUS_ATTEMPT_FAILED',
        'bg-blue-50':  status.reasonCode === 'ALREADY_SENT',
        'bg-amber-50': status.reasonCode === 'OPERATION_IN_PROGRESS' || status.reasonCode === 'NO_RECIPIENTS'
      }">
      <i class="pi text-xl mt-0.5 shrink-0" [ngClass]="{
          'pi-check text-green-500':                status.reasonCode === 'AVAILABLE',
          'pi-times-circle text-red-500':           status.reasonCode === 'REPORTS_NOT_PUBLISHED' || status.reasonCode === 'PREVIOUS_ATTEMPT_FAILED',
          'pi-info-circle text-blue-500':           status.reasonCode === 'ALREADY_SENT',
          'pi-spin pi-spinner text-amber-500':      status.reasonCode === 'OPERATION_IN_PROGRESS',
          'pi-exclamation-triangle text-amber-500': status.reasonCode === 'NO_RECIPIENTS'
        }">
      </i>
      <div>
        <ng-container [ngSwitch]="status.reasonCode">
          <ng-container *ngSwitchCase="'AVAILABLE'">
            <p class="font-bold text-base">Reports are published and ready</p>
            <p class="text-base mt-1">
              All <strong>{{ status.reportingPeriod }}</strong> reports have been confirmed. You're good to send the notification.
            </p>
          </ng-container>
          <ng-container *ngSwitchCase="'REPORTS_NOT_PUBLISHED'">
            <p class="font-bold text-base">Reports are not yet published</p>
            <p class="text-base mt-1">
              <strong>{{ status.reportingPeriod }}</strong> reports are not yet ready. The DB engineer must confirm all reports are published before notifications can be sent.
            </p>
          </ng-container>
          <ng-container *ngSwitchCase="'ALREADY_SENT'">
            <p class="font-bold text-base">Notification already sent</p>
            <p class="text-base mt-1">
              The notification for <strong>{{ status.reportingPeriod }}</strong> has already been sent. It cannot be resent.
            </p>
          </ng-container>
          <ng-container *ngSwitchCase="'PREVIOUS_ATTEMPT_FAILED'">
            <p class="font-bold text-base">Previous attempt failed</p>
            <p class="text-base mt-1">
              A previous notification attempt for <strong>{{ status.reportingPeriod }}</strong> failed. Please contact support to resolve the issue.
            </p>
          </ng-container>
          <ng-container *ngSwitchCase="'OPERATION_IN_PROGRESS'">
            <p class="font-bold text-base">Notification in progress</p>
            <p class="text-base mt-1">
              The notification for <strong>{{ status.reportingPeriod }}</strong> is currently being sent. Please check back shortly.
            </p>
          </ng-container>
          <ng-container *ngSwitchCase="'NO_RECIPIENTS'">
            <p class="font-bold text-base">No recipients found</p>
            <p class="text-base mt-1">
              There are no active credit union portal users to notify for <strong>{{ status.reportingPeriod }}</strong>.
            </p>
          </ng-container>
        </ng-container>
      </div>
    </div>

    <div *ngIf="status.reasonCode === 'AVAILABLE'" class="flex items-start gap-3 bg-amber-50 text-main rounded-lg p-4 mb-6">
      <i class="pi pi-exclamation-circle text-amber-500 text-xl mt-0.5 shrink-0"></i>
      <div>
        <p class="font-extrabold text-base">This can only be sent once</p>
        <p class="text-base mt-1">
          Once sent, you won't be able to resend the notification for this reporting period. Please confirm before proceeding.
        </p>
      </div>
    </div>

    <div *ngIf="sendError" class="flex items-start gap-3 bg-red-50 rounded-lg p-4 mb-4">
      <i class="pi pi-times-circle text-red-500 text-xl mt-0.5 shrink-0"></i>
      <p class="text-sm text-red-700">{{ sendError }}</p>
    </div>

    <div class="flex justify-center gap-3">
      <ng-container *ngIf="status.reasonCode === 'AVAILABLE'; else closeOnly">
        <button
          class="border border-Gray-400 text-Gray-1000 font-medium rounded-lg text-sm px-4 py-2.5 hover:bg-Gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          (click)="close()" [disabled]="isSending">
          {{ sendError ? 'Close' : 'Cancel' }}
        </button>
        <button *ngIf="!sendError"
          class="flex items-center gap-2 bg-main text-white font-medium rounded-lg text-sm px-4 py-2.5 hover:bg-main_hover transition-colors disabled:opacity-75 disabled:cursor-not-allowed"
          (click)="confirmSend()" [disabled]="isSending">
          {{ isSending ? 'Sending' : 'Confirm & Send Notifications' }}
          <i *ngIf="isSending" class="pi pi-spin pi-spinner"></i>
        </button>
      </ng-container>
      <ng-template #closeOnly>
        <button
          class="border border-Gray-400 text-Gray-1000 font-medium rounded-lg text-sm px-4 py-2.5 hover:bg-Gray-100 transition-colors"
          (click)="close()">
          Close
        </button>
      </ng-template>
    </div>
  </ng-container>

  <ng-container *ngIf="result && status">
    <div class="flex items-start gap-3 bg-green-50 rounded-lg p-4 mb-5">
      <i class="pi pi-check text-green-500 text-xl mt-0.5 shrink-0"></i>
      <div class="min-w-0">
        <p class="font-bold text-base">Notification Sent Successfully</p>
        <p class="text-base mt-1">
          All active users have been notified that <strong>{{ status.reportingPeriod }}</strong> reports are ready.
          You can track delivery details on the email gateway dashboard or in the Data Warehouse.
        </p>
        <div class="flex flex-col gap-3 mt-4 text-sm">
          <div>
            <p class="text-gray-500">Request ID</p>
            <p class="font-medium text-main break-all">{{ result.gatewayMessageId }}</p>
          </div>
          <div>
            <p class="text-gray-500">Date Sent</p>
            <p class="font-medium text-main">{{ result.sentAt | date:'MM/dd/yyyy' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center">
      <button
        class="border border-Gray-400 text-Gray-1000 font-medium rounded-lg text-sm px-4 py-2.5 hover:bg-Gray-100 transition-colors"
        (click)="close()">
        Close
      </button>
    </div>
  </ng-container>
</div>
